import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import { m } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"

import {
    Avatar,
    EAvatarSize,
    EModuleExcerptMode,
    EModuleExcerptRsvpButtonPosition,
    EModuleExcerptVariant,
    ESpecialChar,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    ModuleExcerpt,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { richTextRendererUtils as rendererUtils } from "@/shared/rich-text-renderer"
import type { TEmptyAsyncCallback, TEmptyRenderCallback } from "@/shared/types/functions"

import {
    type ICoachGroupSessionParticipantProgressModel as IParticipantModel,
    type ICoachGroupSessionSummaryItemModel as ISummaryModel
} from "./coach.group.api.types"
import styles from "./coach.group.styles"
import uiConfig from "./coach.group.ui.config"
import uiUtils from "./coach.group.ui.utils"

const { summaryRichTextCssIdentifier, getSummaryRichTextClasses } = uiConfig
const { getModuleExcerptUiModel, renderSummaryDetail } = uiUtils
const { isStringPlainText } = rendererUtils
const {
    entities: { individuals: classes }
} = styles

type THeaderProps = {
    isRowExpanded: boolean
    isDataLoading: boolean
    isVisible: boolean
    isDataEmpty: boolean
    isDataError: boolean
    rowProps: IParticipantModel
    renderLoader: TEmptyRenderCallback
}
type TBodyProps = {
    rowProps: IParticipantModel
    onButtonHover(userId: number): Promise<void>
    onButtonAction(userId: number): Promise<void>
}

const Header: FC<THeaderProps> = ({
    isRowExpanded: isExpanded,
    isDataLoading: isLoading,
    isVisible,
    isDataError: isError,
    isDataEmpty,
    rowProps,
    renderLoader
}: THeaderProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    return (
        <div className={classes.row.header.container()}>
            <div
                className={classes.row.header.leftCol.container({
                    isLoading,
                    isError,
                    isDataEmpty,
                    isVisible
                })}
            >
                <div className={classes.row.header.leftCol.profile()}>
                    <Avatar
                        size={EAvatarSize._35}
                        url={rowProps.photo}
                        userData={{
                            firstName: rowProps.firstName,
                            lastName: rowProps.isGuest ? undefined : rowProps.lastName
                        }}
                        isLettered
                    />

                    {breakpoints.xs || breakpoints.sm ? null : (
                        <Typography
                            text={rowProps.firstName}
                            tag={ETypographyTag.Paragraph}
                            size={ETypographySize.Heading2}
                            weight={ETypographyWeight.Medium}
                            family={ETypographyFamily.DmSerif}
                            className={classes.row.header.leftCol.name()}
                        />
                    )}
                </div>

                <ModuleExcerpt
                    mode={EModuleExcerptMode.View}
                    variant={EModuleExcerptVariant.Cropped}
                    rsvpButtonPosition={EModuleExcerptRsvpButtonPosition.Outside}
                    containerClassName={classes.row.header.leftCol.excerpt({ isExpanded })}
                    wrapperClassName={classes.row.header.leftCol.excerptBar()}
                    module={getModuleExcerptUiModel({
                        rank: rowProps.module?.rank,
                        session: rowProps.module?.associatedSession,
                        phases: rowProps.module?.phases,
                        arePhasesHidden: false
                    })}
                />

                <div className={classes.row.header.leftCol.moduleName()}>
                    <Typography
                        text={t("coachSide.groupPage.individuals.row.header.moduleCol.title")}
                        tag={ETypographyTag.Span}
                        size={ETypographySize.Small}
                        weight={ETypographyWeight.Bold}
                    />
                    <Typography
                        text={isEmpty(rowProps.module?.phases) ? t("common.unassigned") : rowProps.module.title}
                        tag={ETypographyTag.Span}
                        size={ETypographySize.Small}
                    />
                </div>
            </div>

            {Boolean(isLoading || isError) && <div className={classes.row.header.rightCol()}>{renderLoader()}</div>}
        </div>
    )
}

const Body: FC<TBodyProps> = ({ rowProps, onButtonAction, onButtonHover }: TBodyProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const handleButtonAction: ReturnType<typeof withPressHandler> = withPressHandler(
        async (): Promise<void> => onButtonAction(rowProps.id)
    )

    const handleButtonHover: TEmptyAsyncCallback = async (): Promise<void> => await onButtonHover(rowProps.id)

    return (
        <div className={classes.row.content.wrapper()}>
            {isNull(rowProps.moduleSummary) || isEmpty(rowProps.moduleSummary?.summaries) ? null : (
                <div className={classes.row.content.moduleSummary.container()}>
                    <div className={classes.row.content.moduleSummary.titleWrapper()}>
                        <Typography
                            text={t("coachSide.groupPage.individuals.row.body.summary.title")}
                            tag={ETypographyTag.Heading3}
                            weight={ETypographyWeight.Medium}
                            family={ETypographyFamily.DmSerif}
                            size={breakpoints.xl || breakpoints.xxl ? ETypographySize.Medium : ETypographySize.Heading2}
                            color={ETypographyColor.Blue200}
                        />

                        <m.button
                            tabIndex={0}
                            type="button"
                            whileFocus={{ scale: 1.075 }}
                            whileHover={{ scale: 1.075 }}
                            transition={{ duration: 0.2, ease: "easeIn" }}
                            aria-label="Navigate to detailed info pages"
                            className={classes.row.content.moduleSummary.worksButton()}
                            onKeyDown={handleButtonAction}
                            onClick={handleButtonAction}
                            onFocus={handleButtonHover}
                            onMouseEnter={handleButtonHover}
                        >
                            {t("coachSide.groupPage.individuals.row.body.module.button")}
                        </m.button>
                    </div>

                    <ul className={classes.row.content.moduleSummary.list()}>
                        {map<ISummaryModel, ReactElement>(
                            rowProps.moduleSummary.summaries,
                            (s: ISummaryModel): ReactElement => (
                                <li
                                    key={`c-gs-ir-${s.title}`}
                                    className={classes.row.content.moduleSummary.listItem({
                                        isInline: isStringPlainText(s.detail)
                                    })}
                                >
                                    <Typography
                                        text={
                                            <Fragment>
                                                {s.title}
                                                {SpecialChars[ESpecialChar.Colon]}
                                                {SpecialChars[ESpecialChar.EmptySpace]}
                                            </Fragment>
                                        }
                                        weight={ETypographyWeight.Bold}
                                        size={ETypographySize.Small}
                                        tag={ETypographyTag.Span}
                                    />

                                    {renderSummaryDetail({
                                        id: summaryRichTextCssIdentifier,
                                        richClasses: getSummaryRichTextClasses(summaryRichTextCssIdentifier, {
                                            isTextSmall: true
                                        }),
                                        content: s.detail,
                                        isTextSmall: true
                                    })}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            )}

            <div className={classes.row.content.moduleProgress.container()}>
                <div className={classes.row.content.moduleProgress.wrapper()}>
                    <Typography
                        text={t("coachSide.groupPage.individuals.row.body.module.title")}
                        weight={ETypographyWeight.Bold}
                        size={ETypographySize.Small}
                        tag={ETypographyTag.Span}
                    />

                    <ModuleExcerpt
                        mode={EModuleExcerptMode.View}
                        variant={EModuleExcerptVariant.Cropped}
                        rsvpButtonPosition={EModuleExcerptRsvpButtonPosition.Outside}
                        containerClassName={classes.row.content.moduleProgress.excerpt()}
                        wrapperClassName={classes.row.content.moduleProgress.excerptBar()}
                        module={getModuleExcerptUiModel({
                            rank: rowProps.module?.rank,
                            session: rowProps.module?.associatedSession,
                            phases: rowProps.module?.phases,
                            arePhasesHidden: isEmpty(rowProps.module?.phases)
                        })}
                    />

                    {!isUndefined(rowProps.module?.phases) && (
                        <Typography
                            tag={ETypographyTag.Paragraph}
                            text={
                                <Fragment>
                                    <Typography
                                        text={
                                            <Fragment>
                                                {t("coachSide.groupPage.individuals.row.body.module.enrollmentTitle")}
                                                {SpecialChars[ESpecialChar.Colon]}
                                            </Fragment>
                                        }
                                        tag={ETypographyTag.Span}
                                        size={ETypographySize.ExtraSmall}
                                        color={ETypographyColor.Dark}
                                        weight={ETypographyWeight.Bold}
                                    />
                                    {SpecialChars[ESpecialChar.EmptySpace]}
                                    <Typography
                                        text={t("coachSide.groupPage.individuals.row.body.module.enrollmentValue", {
                                            rank: rowProps.module?.rank,
                                            length: rowProps.journeyLength
                                        })}
                                        tag={ETypographyTag.Span}
                                        size={ETypographySize.ExtraSmall}
                                        color={ETypographyColor.Dark}
                                    />
                                </Fragment>
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

Header.displayName = "CoachGroupIndividualHeaderComponent"
Body.displayName = "CoachGroupIndividualBodyComponent"

export default { Header, Body }
